/**
 * <buy-now-button> — a self-contained "Buy it now" button.
 *
 * Why this exists: Shopify's native dynamic checkout button (the one the
 * `payment_button` Liquid filter renders) only shows Shop Pay / Apple Pay /
 * Google Pay / PayPal wallets, and those all require an eligible payment
 * provider (Shop Pay needs Shopify Payments, which isn't offered in India).
 * This store runs on Razorpay, so `payment_button` always returns nothing —
 * there is no admin toggle that changes that.
 *
 * This component does the same job without relying on Shopify's wallet
 * infrastructure at all: it adds the current line (variant + quantity, read
 * straight from the product form so it always matches whatever the shopper
 * has selected) to the cart via the standard /cart/add.js endpoint, then
 * sends the browser straight to /checkout. That works with any payment
 * provider, including Razorpay.
 *
 * Markup contract: this element must be inside the product <form>, and must
 * contain one <button data-buy-now-button> and one error element with
 * [data-buy-now-error].
 */
class BuyNowButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-buy-now-button]');
    this.errorEl = this.querySelector('[data-buy-now-error]');

    if (!this.button) return;

    this.button.addEventListener('click', this.handleClick.bind(this));

    /**
     * Clicking Buy Now disables the button while it redirects to /checkout.
     * Pressing back restores this page from the browser's back/forward cache,
     * which replays the DOM exactly as it was left -- i.e. still disabled and
     * greyed out. `pageshow` fires on that restore (event.persisted === true)
     * and is the only reliable hook for it, since `load` does not re-run.
     */
    this.handlePageShow = this.handlePageShow.bind(this);
    window.addEventListener('pageshow', this.handlePageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  /** @param {PageTransitionEvent} event */
  handlePageShow(event) {
    if (!event.persisted) return;
    // Only undo a disable this component caused. A sold-out product renders the
    // button disabled from Liquid, and that must stay disabled.
    if (this.isLoading) this.setLoading(false);
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    event.preventDefault();

    if (this.button.disabled) return;

    const form = this.closest('form');
    if (!form) return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    this.setLoading(true);

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '');
    const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.status) {
          /**
           * Every piece on this store is a one-of-one with inventory 1, so if
           * the shopper already has this exact item in their cart the add call
           * fails with "The maximum quantity of this item is already in your
           * cart." That is not a reason to block Buy Now: the line is already
           * there, so the correct behaviour is to continue to checkout rather
           * than show an error. Confirm against /cart.js before assuming it.
           */
          return this.variantIsInCart(variantId).then((inCart) => {
            if (inCart) return true;
            throw new Error(data.description || data.message || 'Could not add this item to your cart.');
          });
        }
        return true;
      })
      .then(() => {
        window.location.href = this.dataset.checkoutUrl || '/checkout';
      })
      .catch((error) => {
        this.setLoading(false);
        if (this.errorEl) {
          this.errorEl.textContent =
            error && error.message ? error.message : 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
        }
      });
  }

  /**
   * @param {string} variantId
   * @returns {Promise<boolean>} true when that variant already has a line in the cart
   */
  variantIsInCart(variantId) {
    if (!variantId) return Promise.resolve(false);

    return fetch('/cart.js', {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => response.json())
      .then((cart) => {
        const items = (cart && cart.items) || [];
        return items.some((item) => String(item.variant_id) === variantId && item.quantity > 0);
      })
      .catch(() => false);
  }

  /** @param {boolean} isLoading */
  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.button.disabled = isLoading;
    this.button.classList.toggle('is-loading', isLoading);
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
